import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Grid, Button, Chip } from '@mui/material';
import { Code, AutoGraph, ChatBubble, Settings, ArrowForward, ArrowBack, CheckCircleOutline } from '@mui/icons-material';
import SEOHead from '../../components/SEO/SEOHead';
import { useTheme } from '../../contexts/ThemeContext';

const projects = [
  {
    title: "Enterprise Workflow Automation",
    description: "Developed an AI-powered automation system that reduced manual data entry by 80% and saved over 25 hours of work weekly.",
    technologies: ["Python", "OpenAI API", "FastAPI", "PostgreSQL"],
    results: [
      "Reduced process time from 2 hours to 15 minutes",
      "Eliminated 90% of human errors",
      "Scaled to handle 10,000+ transactions daily"
    ]
  },
  {
    title: "AI-Powered Customer Support",
    description: "Built and deployed a custom chatbot that handles 70% of customer inquiries, reducing response time from 2 hours to 2 minutes.",
    technologies: ["React", "Node.js", "TensorFlow", "MongoDB"],
    results: [
      "60% reduction in support tickets",
      "95% customer satisfaction rate",
      "24/7 automated support coverage"
    ]
  },
  {
    title: "Data Analytics Dashboard",
    description: "Created an interactive dashboard that transformed raw business data into actionable insights for executive decision-making.",
    technologies: ["React", "D3.js", "Python", "AWS"],
    results: [
      "Reduced reporting time by 90%",
      "Enabled real-time KPI tracking",
      "Supported data-driven strategy decisions"
    ]
  },
  {
    title: "FacelessFlow AI",
    description: "An innovative AI solution for automated content creation and management, designed to streamline digital presence without the need for on-camera appearances.",
    technologies: ["AI/ML", "Computer Vision", "NLP", "Cloud Infrastructure"],
    results: [
      "Automated content generation pipeline",
      "Seamless integration with existing platforms",
      "Customizable AI models for brand consistency"
    ],
    isProtected: false,
    path: "/contact?project=facelessflow"
  }
];

const skills = [
  { name: "AI/ML Development", icon: <AutoGraph sx={{ fontSize: 40 }} /> },
  { name: "Full-Stack Development", icon: <Code sx={{ fontSize: 40 }} /> },
  { name: "Chatbot Development", icon: <ChatBubble sx={{ fontSize: 40 }} /> },
  { name: "Process Automation", icon: <Settings sx={{ fontSize: 40 }} /> },
];

const testimonials = [
  {
    quote: "The AI solution delivered 3x faster than expected and has become critical to our operations.",
    author: "CTO, Tech Startup",
    role: "Enterprise Client"
  },
  {
    quote: "Exceptional work on our automation project. The team was professional and delivered beyond our expectations.",
    author: "Operations Director, E-commerce",
    role: "Long-term Client"
  }
];

const Portfolio = () => {
  return (
    <Box sx={{ background: '#0b0c0e', color: '#f7f7f8', minHeight: '100vh' }}>
      <SEOHead
        title="Portfolio — AI Case Studies & Projects | VerveAI"
        description="Explore VerveAI's portfolio of custom AI implementations, automation systems, and intelligent software solutions that drive real business results."
        canonical="https://verveai.co/portfolio"
      />

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 12, md: 20 }, pb: { xs: 10, md: 12 } }}>
        <Box sx={{ maxWidth: '900px', mx: 'auto', textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              fontWeight: 800,
              lineHeight: 1.1,
              mb: 3,
              background: 'linear-gradient(135deg, #f7f7f8 0%, #06b6d4 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em'
            }}
          >
            Building AI Solutions That Drive Results
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.1rem', md: '1.35rem' },
              color: 'rgba(245, 247, 250, 0.8)',
              lineHeight: 1.6,
              mb: 6,
              maxWidth: '750px',
              mx: 'auto',
              fontWeight: 400
            }}
          >
            Explore our portfolio of successful AI implementations and custom software solutions that have helped businesses scale efficiently.
          </Typography>

          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            sx={{
              bgcolor: '#06b6d4',
              color: '#0b0c0e',
              px: 4,
              py: 1.75,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(6, 182, 212, 0.3)',
              '&:hover': {
                bgcolor: '#22d3ee',
                boxShadow: '0 12px 32px rgba(6, 182, 212, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Start Your Project
          </Button>
        </Box>
      </Container>

      {/* Projects Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            mb: 6,
            textAlign: 'center'
          }}
        >
          Featured <Box component="span" sx={{ color: '#06b6d4' }}>Projects</Box>
        </Typography>

        <Grid container spacing={4}>
          {projects.map((project, index) => (
            <Grid item xs={12} md={6} lg={6} key={index}>
              <Box sx={{
                bgcolor: 'rgba(20, 25, 35, 0.6)',
                borderRadius: '16px',
                p: 4,
                border: '1px solid rgba(6, 182, 212, 0.2)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: 'rgba(6, 182, 212, 0.5)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                }
              }}>
                <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 2, color: '#f7f7f8' }}>
                  {project.title}
                </Typography>

                <Typography sx={{ color: 'rgba(245, 247, 250, 0.7)', mb: 3, lineHeight: 1.6, flexGrow: 1 }}>
                  {project.description}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
                    KEY RESULTS
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {project.results.map((result, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <CheckCircleOutline sx={{ color: '#00E676', fontSize: 20, mt: 0.25 }} />
                        <Typography sx={{ fontSize: '0.95rem', color: 'rgba(245, 247, 250, 0.9)' }}>
                          {result}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {project.technologies.map((tech, i) => (
                      <Chip
                        key={i}
                        label={tech}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(6, 182, 212, 0.1)',
                          color: '#06b6d4',
                          borderRadius: '6px',
                          fontWeight: 500,
                          border: '1px solid rgba(6, 182, 212, 0.2)'
                        }}
                      />
                    ))}
                  </Box>

                  <Button
                    component={project.isProtected ? Link : 'a'}
                    to={project.isProtected ? project.path : undefined}
                    href={!project.isProtected ? project.path : undefined}
                    target={!project.isProtected ? "_blank" : undefined}
                    rel={!project.isProtected ? "noopener noreferrer" : undefined}
                    variant="outlined"
                    endIcon={<ArrowForward />}
                    sx={{
                      borderColor: 'rgba(6, 182, 212, 0.5)',
                      color: '#06b6d4',
                      textTransform: 'none',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: '#06b6d4',
                        bgcolor: 'rgba(6, 182, 212, 0.1)'
                      }
                    }}
                  >
                    {project.isProtected ? 'View Solution' : 'Learn More'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Technical Expertise */}
      <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 6,
              textAlign: 'center'
            }}
          >
            Technical <Box component="span" sx={{ color: '#06b6d4' }}>Expertise</Box>
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {skills.map((skill, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{
                  textAlign: 'center',
                  p: 4,
                  bgcolor: 'rgba(20, 25, 35, 0.6)',
                  borderRadius: '16px',
                  border: '1px solid rgba(6, 182, 212, 0.1)',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    borderColor: '#06b6d4',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Box sx={{ color: '#06b6d4', mb: 2 }}>
                    {skill.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {skill.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            mb: 8,
            textAlign: 'center'
          }}
        >
          Client <Box component="span" sx={{ color: '#06b6d4' }}>Feedback</Box>
        </Typography>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '24px',
                bgcolor: 'rgba(6, 182, 212, 0.05)',
                border: '1px solid rgba(6, 182, 212, 0.1)',
                position: 'relative'
              }}>
                <Typography sx={{
                  fontSize: '4rem',
                  color: '#06b6d4',
                  opacity: 0.2,
                  fontFamily: 'serif',
                  position: 'absolute',
                  top: 20,
                  left: 20,
                  lineHeight: 1
                }}>
                  "
                </Typography>
                <Typography variant="body1" sx={{
                  fontSize: '1.25rem',
                  fontStyle: 'italic',
                  mb: 4,
                  position: 'relative',
                  zIndex: 1,
                  lineHeight: 1.6
                }}>
                  {testimonial.quote}
                </Typography>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f7f7f8' }}>
                    {testimonial.author}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#06b6d4', fontWeight: 500 }}>
                    {testimonial.role}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'rgba(6, 182, 212, 0.1)', py: 10 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            mb: 3
          }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography variant="h6" sx={{
            mb: 5,
            color: 'rgba(245, 247, 250, 0.8)',
            fontWeight: 400,
            lineHeight: 1.6
          }}>
            Let's discuss how customized AI solutions can work for you.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            component={Link}
            to="/contact"
            sx={{
              bgcolor: '#06b6d4',
              color: '#0b0c0e',
              px: 5,
              py: 2,
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '1.2rem',
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(6, 182, 212, 0.3)',
              '&:hover': {
                bgcolor: '#22d3ee',
                boxShadow: '0 12px 32px rgba(6, 182, 212, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Get Started Today
          </Button>
        </Container>
      </Box>

      {/* Back to Home CTA */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBack />}
          sx={{
            color: 'rgba(245, 247, 250, 0.6)',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              color: '#06b6d4',
              bgcolor: 'transparent'
            }
          }}
        >
          Back to Home
        </Button>
      </Container>
    </Box>
  );
};

export default Portfolio;
